import { useCallback, useEffect, useRef, useState } from 'react'

import { API_BASE_URL, apiRequest, apiUpload, getAccessToken } from './client'

export type ChatMessageStatus = 'sending' | 'failed'

export type ChatAttachment = {
  id: number
  filename: string
  contentType: string | null
  sizeBytes: number
}

export type ChatMessage = {
  id: number
  threadId: number
  senderUserProfileId: number
  senderOrgId: number
  body: string
  createdAt: string
  attachment?: ChatAttachment | null
  /** Только для оптимистичных сообщений; серверные приходят без статуса. */
  status?: ChatMessageStatus
  /** Клиентский идентификатор оптимистичного сообщения для последующей замены. */
  clientId?: string
  /** Локальное имя прикреплённого файла для оптимистичного сообщения. */
  pendingFileName?: string
}

type ChatAttachmentRaw = {
  id: number
  filename?: string
  contentType?: string | null
  content_type?: string | null
  sizeBytes?: number
  size_bytes?: number
}

// Терпимость к обоим написаниям на проводе: API нормализуется к snake_case,
// поэтому читаем camelCase, а при его отсутствии — snake_case.
type ChatMessageRaw = ChatMessage & {
  thread_id?: number
  sender_user_profile_id?: number
  sender_org_id?: number
  created_at?: string
  attachment?: ChatAttachmentRaw | null
}

const normalizeAttachment = (raw: ChatAttachmentRaw | null | undefined): ChatAttachment | null => {
  if (!raw) {
    return null
  }
  return {
    id: raw.id,
    filename: raw.filename ?? 'файл',
    contentType: raw.contentType ?? raw.content_type ?? null,
    sizeBytes: raw.sizeBytes ?? raw.size_bytes ?? 0,
  }
}

const normalizeChatMessage = (raw: ChatMessageRaw): ChatMessage => ({
  id: raw.id,
  threadId: raw.threadId ?? raw.thread_id ?? 0,
  senderUserProfileId: raw.senderUserProfileId ?? raw.sender_user_profile_id ?? 0,
  senderOrgId: raw.senderOrgId ?? raw.sender_org_id ?? 0,
  body: raw.body,
  createdAt: raw.createdAt ?? raw.created_at ?? new Date(0).toISOString(),
  attachment: normalizeAttachment(raw.attachment),
})

type ChatMessagesResponse = {
  items: ChatMessageRaw[]
}

const POLL_INTERVAL_MS = 4000

const partnerThreadPath = (organizationId: number, counterpartyOrganizationId: number) =>
  `/organizations/${organizationId}/partner-threads/${counterpartyOrganizationId}`

export const fetchChatMessages = (
  organizationId: number,
  counterpartyOrganizationId: number,
  options: { afterId?: number; beforeId?: number; limit?: number } = {},
) => {
  const params = new URLSearchParams({ limit: String(options.limit ?? 50) })
  if (options.afterId != null) {
    params.set('afterId', String(options.afterId))
  }
  if (options.beforeId != null) {
    params.set('beforeId', String(options.beforeId))
  }
  return apiRequest<ChatMessagesResponse>(
    `${partnerThreadPath(organizationId, counterpartyOrganizationId)}/messages?${params}`,
  ).then((response) => ({ items: (response.items ?? []).map(normalizeChatMessage) }))
}

export const sendChatMessage = (
  organizationId: number,
  counterpartyOrganizationId: number,
  text: string,
) =>
  apiRequest<ChatMessageRaw>(`${partnerThreadPath(organizationId, counterpartyOrganizationId)}/messages`, {
    method: 'POST',
    body: { text },
  }).then(normalizeChatMessage)

export const sendChatAttachment = (
  organizationId: number,
  counterpartyOrganizationId: number,
  file: File,
  text: string,
) => {
  const form = new FormData()
  form.append('file', file)
  if (text.trim()) {
    form.append('text', text.trim())
  }
  return apiUpload<ChatMessageRaw>(
    `${partnerThreadPath(organizationId, counterpartyOrganizationId)}/messages/attachment`,
    form,
  ).then(normalizeChatMessage)
}

/** Скачивает вложение с авторизацией и отдаёт временный object URL (нужно освободить через revokeObjectURL). */
export const fetchAttachmentObjectUrl = async (
  organizationId: number,
  counterpartyOrganizationId: number,
  attachmentId: number,
): Promise<string> => {
  const token = getAccessToken()
  const response = await fetch(
    `${API_BASE_URL}${partnerThreadPath(organizationId, counterpartyOrganizationId)}/attachments/${attachmentId}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  )
  if (!response.ok) {
    throw new Error('Не удалось загрузить вложение')
  }
  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

/** Сливает серверные сообщения по id, сохраняя оптимистичные (с clientId) в хвосте. */
const mergeServerMessages = (existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] => {
  const optimistic = existing.filter((message) => message.clientId)
  const byId = new Map(existing.filter((message) => !message.clientId).map((m) => [m.id, m]))
  for (const message of incoming) {
    byId.set(message.id, message)
  }
  const serverSorted = [...byId.values()].sort((a, b) => a.id - b.id)
  return [...serverSorted, ...optimistic]
}

let optimisticSeq = 0
const nextOptimisticId = (): number => {
  optimisticSeq += 1
  return -optimisticSeq
}

export const useChatPolling = (
  organizationId: number,
  counterpartyOrganizationId: number | null,
  active: boolean,
) => {
  const PAGE_SIZE = 50
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const lastIdRef = useRef<number | null>(null)
  const pendingFilesRef = useRef<Map<string, File>>(new Map())

  const firstServerId = useCallback(
    () => messages.find((message) => !message.clientId)?.id ?? null,
    [messages],
  )

  const loadMessages = useCallback(async (mode: 'initial' | 'poll') => {
    if (counterpartyOrganizationId == null) {
      return
    }
    if (mode === 'initial') {
      setLoading(true)
      setError(null)
    }
    try {
      const afterId = mode === 'poll' ? (lastIdRef.current ?? undefined) : undefined
      const response = await fetchChatMessages(organizationId, counterpartyOrganizationId, {
        afterId,
        limit: PAGE_SIZE,
      })
      if (mode === 'initial') {
        setHasMore(response.items.length >= PAGE_SIZE)
      }
      setMessages((current) => {
        const next = mode === 'initial' ? response.items : mergeServerMessages(current, response.items)
        const lastServer = [...next].reverse().find((message) => !message.clientId)
        if (lastServer) {
          lastIdRef.current = lastServer.id
        }
        return next
      })
    } catch (loadError) {
      if (mode === 'initial') {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить сообщения')
      }
    } finally {
      if (mode === 'initial') {
        setLoading(false)
      }
    }
  }, [counterpartyOrganizationId, organizationId])

  const loadOlder = useCallback(async () => {
    if (counterpartyOrganizationId == null || loadingOlder) {
      return
    }
    const beforeId = firstServerId()
    if (beforeId == null) {
      return
    }
    setLoadingOlder(true)
    try {
      const response = await fetchChatMessages(organizationId, counterpartyOrganizationId, {
        beforeId,
        limit: PAGE_SIZE,
      })
      setHasMore(response.items.length >= PAGE_SIZE)
      setMessages((current) => mergeServerMessages(current, response.items))
    } catch {
      // Подгрузка истории — best effort; кнопка «Загрузить ещё» остаётся доступной.
    } finally {
      setLoadingOlder(false)
    }
  }, [counterpartyOrganizationId, firstServerId, loadingOlder, organizationId])

  useEffect(() => {
    if (counterpartyOrganizationId == null) {
      setMessages([])
      lastIdRef.current = null
      setError(null)
      setHasMore(false)
      return
    }

    lastIdRef.current = null
    void loadMessages('initial')
  }, [counterpartyOrganizationId, loadMessages])

  useEffect(() => {
    if (!active || counterpartyOrganizationId == null) {
      return
    }

    const timer = window.setInterval(() => {
      void loadMessages('poll')
    }, POLL_INTERVAL_MS)

    return () => {
      window.clearInterval(timer)
    }
  }, [active, counterpartyOrganizationId, loadMessages])

  const deliver = useCallback(
    async (clientId: string, body: string) => {
      if (counterpartyOrganizationId == null) {
        return
      }
      const file = pendingFilesRef.current.get(clientId) ?? null
      setSending(true)
      try {
        const created = file
          ? await sendChatAttachment(organizationId, counterpartyOrganizationId, file, body)
          : await sendChatMessage(organizationId, counterpartyOrganizationId, body)
        pendingFilesRef.current.delete(clientId)
        setMessages((current) => {
          const withoutOptimistic = current.filter((message) => message.clientId !== clientId)
          return mergeServerMessages(withoutOptimistic, [created])
        })
        lastIdRef.current = created.id
      } catch {
        // Оставляем сообщение в ленте с пометкой «не отправлено» — доступен повтор.
        setMessages((current) =>
          current.map((message) =>
            message.clientId === clientId ? { ...message, status: 'failed' } : message,
          ),
        )
      } finally {
        setSending(false)
      }
    },
    [counterpartyOrganizationId, organizationId],
  )

  /** Вставляет сообщение в ленту сразу и отправляет в фоне. Возвращает false, только если нечего слать. */
  const handleSend = (text: string, file?: File | null): boolean => {
    if (counterpartyOrganizationId == null) {
      return false
    }
    const trimmed = text.trim()
    if (!trimmed && !file) {
      return false
    }
    setError(null)
    const clientId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `opt-${Date.now()}-${optimisticSeq}`
    if (file) {
      pendingFilesRef.current.set(clientId, file)
    }
    const optimistic: ChatMessage = {
      id: nextOptimisticId(),
      threadId: 0,
      senderUserProfileId: 0,
      senderOrgId: organizationId,
      body: trimmed,
      createdAt: new Date().toISOString(),
      status: 'sending',
      clientId,
      pendingFileName: file?.name,
    }
    setMessages((current) => [...current, optimistic])
    void deliver(clientId, trimmed)
    return true
  }

  const retryMessage = (message: ChatMessage) => {
    if (!message.clientId) {
      return
    }
    const clientId = message.clientId
    setMessages((current) =>
      current.map((entry) =>
        entry.clientId === clientId ? { ...entry, status: 'sending' } : entry,
      ),
    )
    void deliver(clientId, message.body)
  }

  const isOwnMessage = (message: ChatMessage) => message.senderOrgId === organizationId

  return {
    messages,
    loading,
    loadingOlder,
    hasMore,
    error,
    sending,
    handleSend,
    retryMessage,
    loadOlder,
    isOwnMessage,
  }
}
